import React, { useCallback, useState } from 'react';
import { message, Tooltip } from 'antd';

import { CLIPBOARD, EYE, EYE_SLASH } from '../icons';
import { formatValue } from '../items/itemValueDisplay';
import { copyTextToClipboard } from '../../utils/clipboard';
import { MASK_TEXT, hasMaskableValue } from '../../utils/maskedElements';

/**
 * A MASKED value (KPR-1348, utils/maskedElements.js): eight dots, whatever
 * the value, followed by two triggers — **Show** and **Copy** — on every read
 * surface (tile, table, page, record view, share link, History).
 *
 *   - Show reveals the value IN PLACE and turns into Hide. The state is this
 *     component's own, so the value masks again when the person leaves the
 *     item (the component unmounts) or reloads; nothing is remembered.
 *   - Copy puts the real value on the clipboard WITHOUT showing it, and says
 *     "Copied"; a clipboard the browser refuses says so as an error, and
 *     nothing claims success.
 *   - Both are real <button>s with a label a screen reader says ("Show
 *     Combination", "Copy Combination"), reachable by Tab and pressed with
 *     Enter or Space; Show carries `aria-pressed`.
 *   - Neither sends anything: showing and copying are the viewer's own and are
 *     never recorded (no History row, no request).
 *   - An empty element is the dash every empty value is, never a mask.
 *
 * The triggers wear the "values carry their actions" glyph (docs/
 * platform-standards.md §4) and stop the click and key press from reaching
 * the tile or row behind them, which would otherwise open the item.
 *
 * `text` is the revealed reading when the caller has its own (History's
 * display strings); by default the value reads as formatValue reads it.
 * Never call this secure: it is a display setting.
 */

const stop = event => event.stopPropagation();

const labelOf = (element, label) => label || element?.label?.singular || element?.name || 'value';

// What a reveal is FOR: the record (when the caller names it) and the value.
// A read view reuses its rows when the person moves to another item, so a
// reveal is kept against this token and reads as hidden the moment either
// changes — in the same render, with no frame where the next item's value
// shows.
const revealToken = (resetKey, value) => `${resetKey ?? ''}\u0000${typeof value === 'object' ? JSON.stringify(value) : String(value)}`;

export default function MaskedValue({ element, value, text = undefined, prefs = undefined, label = undefined, className = '', empty = '—', resetKey = undefined }) {
	const token = revealToken(resetKey, value);
	const [shownFor, setShownFor] = useState(null);
	const shown = shownFor === token;
	const name = labelOf(element, label);
	const revealed = text !== undefined ? `${text ?? ''}` : `${formatValue(element, value, { ...(prefs || {}), reveal: true }) ?? ''}`;

	const handleShow = useCallback((event) => {
		event.stopPropagation();
		setShownFor(current => (current === token ? null : token));
	}, [token]);

	const handleCopy = useCallback(async (event) => {
		event.stopPropagation();
		if (await copyTextToClipboard(revealed)) message.success(`${name} copied`);
		else message.error(`Couldn't copy ${name}. Your browser didn't allow it.`);
	}, [revealed, name]);

	if (!hasMaskableValue(value) || !revealed) return empty;

	const ShowIcon = shown ? EYE_SLASH : EYE;
	return (
		<span className={`masked-value ${shown ? 'masked-value--shown' : ''} ${className}`.trim().replace(/\s+/g, ' ')}>
			<span className="masked-value__text">
				{shown ? revealed : <span className="masked-value__mask" aria-label={`${name} hidden`} role="img">{MASK_TEXT}</span>}
			</span>
			<span className="masked-value__actions" onClick={stop} onKeyDown={stop} role="presentation">
				<Tooltip title={shown ? 'Hide' : 'Show'}>
					<button
						type="button"
						className="value-action__trigger masked-value__trigger"
						aria-label={`${shown ? 'Hide' : 'Show'} ${name}`}
						aria-pressed={shown}
						onClick={handleShow}
					>
						<ShowIcon aria-hidden="true" />
					</button>
				</Tooltip>
				<Tooltip title="Copy">
					<button
						type="button"
						className="value-action__trigger masked-value__trigger"
						aria-label={`Copy ${name}`}
						onClick={handleCopy}
					>
						<CLIPBOARD aria-hidden="true" />
					</button>
				</Tooltip>
			</span>
		</span>
	);
}
